/**
 * Vertical scrolling drives a pinned sequence of horizontal rows.
 * The template stays stacked without JS, in previews and with reduced motion.
 *
 * Pinned rows all share the section's position, so the theme's anchor
 * scrolling cannot find one by where it sits on the page. It hands links to
 * a row over to this script instead — see goToRow().
 */

const SCROLL_LAYOUT = 'content-rows--scrolling';
const ROW_HOLD = 0.3;

// Set on <html> while something else owns where the page is sitting — a
// scroll on its way somewhere, or a popup covering the page (see
// resources/js/global.js). rebuild() repositions the page and measures the
// layout to decide whether the rows still fit, so it waits: through a scroll
// it would end it early, and behind a popup it would measure a page that
// overflow:hidden has made unscrollable.
const SCROLL_HOLD_ATTRIBUTE = 'data-wpbase-scroll-hold';
const SCROLL_RELEASED_EVENT = 'wpbase:scroll-released';

// The theme's anchor scrolling offers each target with the first before it
// moves the page, and expects the second once the page is there.
const SCROLL_REQUEST_EVENT = 'wpbase:scroll-request';
const HASH_TARGET_EVENT = 'wpbase:hash-target';

// Seconds. The scroll matches SCROLL_DURATION in global.js; the slide is for
// changing rows while the block is already on screen.
const SCROLL_DURATION = 1.5;
const SLIDE_DURATION = 0.9;

// The reader scrolling for themselves takes over from a link.
const READER_INPUT = ['wheel', 'touchstart'];

// Lenis has to make the move while it is running, or its own idea of the
// position drifts from the page's. Without a duration, or Lenis, this jumps.
function scrollPage(top, duration = 0, onComplete) {
    if (window.lenis && duration) {
        window.lenis.scrollTo(top, { duration, force: true, onComplete });
        return;
    }

    if (window.lenis) {
        window.lenis.scrollTo(top, { immediate: true, force: true });
    } else {
        window.scrollTo({ top, left: 0, behavior: 'instant' });
    }

    onComplete?.();
}

function createSequence(block, gsap, ScrollTrigger, revealRow) {
    const rows = Array.from(block.querySelectorAll('.content-rows__row'));
    let animationContext = null;
    let scrollTrigger = null;
    // While a link takes the reader to a row, the rows show that row rather
    // than the one the scroll position gives them. Counted in rows.
    let shown = null;
    let showTween = null;

    function destroy() {
        showTween?.kill();
        showTween = null;
        shown = null;
        // A switched-off trigger skips its own clean-up and would leave its pin
        // spacer behind, so it is switched back on (without a refresh) first.
        if (scrollTrigger && !scrollTrigger.enabled) scrollTrigger.enable(false, false);
        animationContext?.revert();
        animationContext = null;
        scrollTrigger = null;
        block.classList.remove(SCROLL_LAYOUT);
    }

    // Timeline time for a position counted in rows. The pauses are skipped,
    // so a slide across several rows does not stop at each one.
    function timeAt(position) {
        const index = Math.floor(position);
        return Math.min(index * (1 + ROW_HOLD) + position - index, scrollTrigger.animation.duration());
    }

    // The reverse, with a row's pause counting as that row.
    function positionAt(time) {
        const index = Math.floor(time / (1 + ROW_HOLD));
        return Math.min(index + Math.min(1, time - index * (1 + ROW_HOLD)), rows.length - 1);
    }

    function applyShown() {
        if (shown && scrollTrigger) scrollTrigger.animation.time(timeAt(shown.position));
    }

    // Shows a row whatever the scroll position, sliding to it over `duration`.
    function show(row, duration, onComplete) {
        showTween?.kill();
        shown = { position: shown ? shown.position : positionAt(scrollTrigger.animation.time()) };

        if (!duration) {
            shown.position = rows.indexOf(row);
            applyShown();
            onComplete?.();
            return;
        }

        showTween = gsap.to(shown, {
            position: rows.indexOf(row),
            duration,
            ease: 'power2.inOut',
            onUpdate: applyShown,
            onComplete,
        });
    }

    // Hands the rows back to the scroll position.
    function release() {
        showTween?.kill();
        showTween = null;
        if (!shown) return;

        shown = null;
        if (scrollTrigger) scrollTrigger.animation.progress(scrollTrigger.progress);
    }

    // Switches the pin off where it stands, so a scroll on its way to the
    // block cannot pin it early (anticipatePin) or with an out-of-date start.
    function suspend() {
        scrollTrigger?.disable(false);
    }

    function resume() {
        if (scrollTrigger && !scrollTrigger.enabled) scrollTrigger.enable();
    }

    // The scroll position that shows a row in place: the pin start for the
    // first, and halfway through the pause after each later row slides in, so
    // rounding or a small layout shift still leaves it at rest. Null while the
    // block is stacked.
    function rowTop(row) {
        const index = rows.indexOf(row);
        if (!scrollTrigger || index < 0) return null;

        const timeline = scrollTrigger.animation;
        const time = timeline.labels[`row-${index}`] + (index ? ROW_HOLD / 2 : 0);

        return scrollTrigger.start + (time / timeline.duration()) * (scrollTrigger.end - scrollTrigger.start);
    }

    function inView() {
        const { top, bottom } = block.getBoundingClientRect();
        return bottom > 0 && top < window.innerHeight;
    }

    // Where the bottom of the block's scroll distance (its pin spacer, once
    // pinned) sits in the viewport. Read off the page as it is laid out now,
    // which the trigger's own numbers can lag behind.
    function spacerBottom() {
        const spacer = block.parentElement?.classList.contains('pin-spacer') ? block.parentElement : block;
        return spacer.getBoundingClientRect().bottom;
    }

    function build() {
        destroy();
        if (rows.length < 2) return;

        block.classList.add(SCROLL_LAYOUT);

        // Reserve room for the sticky header. Tall copy must remain readable
        // through ordinary vertical scrolling, including at browser zoom.
        const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
        const adminHeight = document.getElementById('wpadminbar')?.offsetHeight || 0;
        const availableHeight = window.innerHeight - headerHeight - adminHeight - 24;

        if (block.offsetHeight > availableHeight) {
            block.classList.remove(SCROLL_LAYOUT);
            return;
        }

        animationContext = gsap.context(() => {
            // Fill the area below the header so earlier page content does not
            // scroll away and leave an empty strip above the pinned section.
            gsap.set(block, { minHeight: window.innerHeight - headerHeight - adminHeight });
            gsap.set(rows.slice(1), { x: () => block.clientWidth });

            const timeline = gsap.timeline({
                defaults: { duration: 1, ease: 'none' },
                scrollTrigger: {
                    trigger: block,
                    // Pin once the first row's full stage has entered the viewport.
                    start: 'bottom bottom',
                    end: () => `+=${(rows.length - 1) * window.innerHeight * (1 + ROW_HOLD)}`,
                    pin: true,
                    pinSpacing: true,
                    scrub: true,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    // Anything that moves the timeline while a row is being
                    // shown, like a refresh, is put back.
                    onUpdate: applyShown,
                    onRefresh: applyShown,
                },
            });

            timeline.addLabel('row-0', 0);

            rows.slice(1).forEach((row, index) => {
                timeline
                    .to(rows[index], { x: () => -block.clientWidth })
                    .fromTo(row, { x: () => block.clientWidth }, { x: 0 }, '<')
                    .addLabel(`row-${index + 1}`)
                    // A little scroll distance with the row at rest gives the
                    // reader room to pause before the next transition or exit.
                    .to({}, { duration: ROW_HOLD });
            });
            scrollTrigger = timeline.scrollTrigger;

            // Keep links in their natural tab order. Focusing a row brings it
            // into view at its matching scroll position, also with Shift+Tab.
            function revealFocusedRow(event) {
                const row = event.target.closest('.content-rows__row');
                if (!rows.includes(row) || Math.abs(gsap.getProperty(row, 'x')) < 1) return;

                revealRow(row);
            }

            block.addEventListener('focusin', revealFocusedRow);
            return () => block.removeEventListener('focusin', revealFocusedRow);
        }, block);
    }

    return {
        block,
        rows,
        build,
        destroy,
        show,
        release,
        suspend,
        resume,
        rowTop,
        inView,
        spacerBottom,
        get trigger() { return scrollTrigger; },
        get suspended() { return !!scrollTrigger && !scrollTrigger.enabled; },
    };
}

function initContentRows() {
    const { gsap, ScrollTrigger } = window;
    const blocks = Array.from(document.querySelectorAll('[data-content-rows-scroll]'));
    if (!gsap || !ScrollTrigger || !blocks.length) return;

    gsap.registerPlugin(ScrollTrigger);

    // Match the block's single-column breakpoint. matchMedia also restores the
    // stacked layout immediately if the motion preference changes at runtime.
    gsap.matchMedia().add('screen and (min-width: 769px) and (prefers-reduced-motion: no-preference)', () => {
        const sequences = blocks.map((block) => createSequence(block, gsap, ScrollTrigger, (row) => goToRow(row)));
        const header = document.querySelector('.header');
        let resizeTimer;
        let disposed = false;
        let rebuildPending = false;
        let builtHeader = null;
        // The row a link is taking the reader to. Kept after arriving, so a
        // rebuild can put the reader back on it.
        let journey = null;

        function endJourney() {
            if (!journey) return;

            journey.sequence.release();
            journey.sequence.resume();
            journey = null;
        }

        // The pinned row a link target is in. The block's own ID stands for
        // its first row: pinned, the section sits at the end of its spacer.
        function rowFor(target) {
            if (!(target instanceof Element)) return null;

            const row = target.matches('[data-content-rows-scroll]')
                ? target.querySelector('.content-rows__row')
                : target.closest('.content-rows__row');

            return sequences.some((sequence) => sequence.trigger && sequence.rows.includes(row)) ? row : null;
        }

        // Out of date once a rebuild has been asked for, or the header has
        // changed since the last one: it leaves the flow when it goes sticky,
        // and takes everything below it up with it.
        function pinsStale() {
            return rebuildPending
                || (header !== null && header.className !== builtHeader)
                || sequences.some((sequence) => sequence.suspended);
        }

        // Steps from the edge of the pin into the row's own position, which
        // looks exactly the same, and hands the rows back to the scroll.
        function stepIn(current) {
            if (journey !== current) return;
            if (pinsStale()) rebuildPins();

            const top = current.sequence.rowTop(current.row);

            // Rebuilt too tall to pin, so the rows are stacked after all.
            if (typeof top !== 'number') {
                endJourney();
                return;
            }

            scrollPage(top);
            ScrollTrigger.update();
            current.sequence.release();
            current.top = top;

            if (!current.arrived) {
                current.arrived = true;
                current.target?.dispatchEvent(new CustomEvent(HASH_TARGET_EVENT, { bubbles: true }));
            }
        }

        // A scroll straight to a row would slide past every row ahead of it,
        // as the rows only show the one the scroll position gives them. So the
        // row is shown first (slid to, if the block is on screen), the page
        // scrolls only as far as the edge of the pin, and then steps in.
        function goToRow(row, { target = null, duration = 0, slide = false } = {}) {
            const sequence = sequences.find((candidate) => candidate.rows.includes(row));
            if (!sequence?.trigger) return false;

            if (journey && journey.sequence !== sequence) endJourney();

            const current = { row, target, sequence, arrived: false, approaching: false, top: null };
            journey = current;

            if (!duration) {
                stepIn(current);
                return true;
            }

            const { start, end } = sequence.trigger;
            const scroll = window.scrollY;
            let slid = false;
            let scrolled = false;

            function settle() {
                if (slid && scrolled && !current.arrived) stepIn(current);
            }

            const slideDone = () => {
                slid = true;
                settle();
            };
            const scrollDone = () => {
                scrolled = true;
                settle();
            };

            // After a rebuild has replaced the rows the slide was moving.
            current.reshow = () => sequence.show(row, 0, slideDone);

            sequence.show(row, slide && sequence.inView() ? SLIDE_DURATION : 0, slideDone);

            // Pinned and on screen already: the slide is the whole move.
            if (scroll >= start && scroll <= end) {
                scrollDone();
                return true;
            }

            // The pin is switched off for the way there. From above, the edge
            // is where the block sits flush under the header, worked out the
            // way the theme's anchor scrolling does it: in the layout the page
            // will have once the header has gone sticky on the way down.
            current.approaching = true;
            sequence.suspend();

            const adminHeight = document.getElementById('wpadminbar')?.offsetHeight || 0;
            const edge = scroll > end
                ? end + 1
                : window.wpbaseScroll?.restingScrollTop?.(sequence.block, adminHeight) ?? start - 1;

            scrollPage(edge, duration, scrollDone);
            // Lenis can be stopped short without saying so; step in regardless.
            window.setTimeout(scrollDone, duration * 1000 + 250);

            return true;
        }

        function rebuildPins() {
            rebuildPending = false;
            window.clearTimeout(resizeTimer);

            // Removing pin spacers can briefly shorten the document enough for
            // the browser to clamp scrollY backwards. Keep its height until all
            // sequences have been rebuilt, then restore the authored body style.
            const body = document.body;
            const minHeight = body.style.getPropertyValue('min-height');
            const minHeightPriority = body.style.getPropertyPriority('min-height');
            body.style.setProperty('min-height', `${document.documentElement.scrollHeight}px`);

            // Remove all spacers first so multiple instances measure in page order.
            sequences.forEach((sequence) => sequence.destroy());
            sequences.forEach((sequence) => sequence.build());
            if (minHeight) {
                body.style.setProperty('min-height', minHeight, minHeightPriority);
            } else {
                body.style.removeProperty('min-height');
            }

            ScrollTrigger.refresh();
            window.lenis?.resize();
            builtHeader = header?.className ?? null;
        }

        function rebuild() {
            const scrollTop = window.scrollY;
            // Remember progress in the last sequence we reached, or where the
            // end of it is on screen. Resizing must not replay a completed row.
            const previous = sequences.reduce((position, sequence) => {
                const trigger = sequence.trigger;
                if (!trigger || scrollTop < trigger.start) return position;

                return {
                    sequence,
                    progress: Math.min(1, (scrollTop - trigger.start) / (trigger.end - trigger.start)),
                    past: scrollTop > trigger.end,
                    bottom: sequence.spacerBottom(),
                };
            }, null);

            rebuildPins();

            if (journey && !journey.arrived) {
                // Still on its way: the rebuild replaced the rows it was
                // showing. The scroll carries on to the same edge.
                if (journey.approaching) {
                    journey.sequence.suspend();
                    journey.reshow();
                } else {
                    stepIn(journey);
                }
                return;
            }

            // Arrived: put the reader back on the row, unless they have left
            // it some way READER_INPUT does not see, like the scrollbar.
            if (journey) {
                if (Math.abs(scrollTop - journey.top) < window.innerHeight / 2) {
                    stepIn(journey);
                    return;
                }
                journey = null;
            }

            // Past the end, the bottom of the block's scroll distance stays put
            // on screen, and everything below it with it. Measuring the page
            // rather than trusting the old trigger matters after an anchor
            // scroll: rebuilds wait for it to finish, and the header has left
            // the flow along the way, moving the page by its full height.
            const trigger = previous?.sequence.trigger;
            let target = scrollTop;
            if (previous?.past) {
                target = window.scrollY + previous.sequence.spacerBottom() - previous.bottom;
            } else if (trigger) {
                target = trigger.start + previous.progress * (trigger.end - trigger.start);
            }
            const top = Math.max(0, Math.min(target, ScrollTrigger.maxScroll(window)));

            if (Math.abs(window.scrollY - top) > 1) {
                scrollPage(top);
                ScrollTrigger.update();
            }
        }

        let waitingForScrollEnd = false;

        function scheduleRebuild() {
            if (disposed) return;
            window.clearTimeout(resizeTimer);
            rebuildPending = true;

            if (document.documentElement.hasAttribute(SCROLL_HOLD_ATTRIBUTE)) {
                // One listener however many rebuilds are asked for meanwhile;
                // they all collapse into the single one scheduled on release.
                if (!waitingForScrollEnd) {
                    waitingForScrollEnd = true;
                    document.addEventListener(SCROLL_RELEASED_EVENT, () => {
                        waitingForScrollEnd = false;
                        scheduleRebuild();
                    }, { once: true });
                }
                return;
            }

            resizeTimer = window.setTimeout(rebuild, 150);
        }

        // The theme's anchor scrolling, from a link or a hash in the URL,
        // offers its target here first. Rows are taken over; any other target
        // ends a journey to a row, as the page is going somewhere else.
        function onScrollRequest(event) {
            const row = rowFor(event.target);

            if (row && goToRow(row, { target: event.target, duration: SCROLL_DURATION, slide: true })) {
                event.preventDefault();
            } else {
                endJourney();
            }
        }

        rebuild();

        // Font/image loads and changes in authored copy can change whether a
        // row fits. Row dimensions stay the same in either layout.
        const sizes = new WeakMap();
        const observer = typeof ResizeObserver === 'function'
            ? new ResizeObserver((entries) => {
                let changed = false;
                entries.forEach(({ target, contentRect }) => {
                    const previous = sizes.get(target);
                    // Pinning can introduce subpixel rounding. Ignore it and
                    // the initial observation instead of restarting the timeline.
                    if (!previous) {
                        sizes.set(target, contentRect);
                    } else if (Math.abs(previous.width - contentRect.width) >= 1 || Math.abs(previous.height - contentRect.height) >= 1) {
                        sizes.set(target, contentRect);
                        changed = true;
                    }
                });
                if (changed) scheduleRebuild();
            })
            : null;
        sequences.forEach(({ rows }) => rows.forEach((row) => observer?.observe(row)));

        // The theme takes the header out of document flow when it becomes
        // sticky. Measure again once its slide animation has finished.
        const headerObserver = header ? new MutationObserver(() => {
            if (!header.classList.contains('header--sticky') || header.classList.contains('header--show')) {
                scheduleRebuild();
            }
        }) : null;
        if (header) headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });

        window.addEventListener('resize', scheduleRebuild);
        window.addEventListener('load', scheduleRebuild);
        document.fonts?.ready.then(scheduleRebuild);
        document.addEventListener(SCROLL_REQUEST_EVENT, onScrollRequest);
        READER_INPUT.forEach((type) => window.addEventListener(type, endJourney, { passive: true }));

        return () => {
            disposed = true;
            window.clearTimeout(resizeTimer);
            observer?.disconnect();
            headerObserver?.disconnect();
            window.removeEventListener('resize', scheduleRebuild);
            window.removeEventListener('load', scheduleRebuild);
            document.removeEventListener(SCROLL_REQUEST_EVENT, onScrollRequest);
            READER_INPUT.forEach((type) => window.removeEventListener(type, endJourney));
            journey = null;
            sequences.forEach((sequence) => sequence.destroy());
            window.lenis?.resize();
        };
    });
}

// Deferred GSAP scripts have executed by DOMContentLoaded. One frame also lets
// the global script finish setting up Lenis before we measure and add spacers.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => requestAnimationFrame(initContentRows), { once: true });
} else {
    requestAnimationFrame(initContentRows);
}
